import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
// Removed FaTimes import due to TypeScript issues
import { useStripe as useStripeContext } from '../../contexts/StripeContext';
import { useUser } from '../../contexts/UserContext';
import { config } from '../../config';
import { authService } from '../../services/auth';

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 32px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid #333;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #999;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: white;
  }
`;

const TierSummary = styled.div`
  background: #2a2a2a;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
  border: 1px solid #333;
`;

const TierName = styled.h3`
  color: white;
  font-size: 1.25rem;
  margin: 0 0 8px 0;
`;

const TierPrice = styled.div`
  color: #4ade80;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const TierPeriod = styled.div`
  color: #999;
  font-size: 0.9rem;
`;

const CouponBadge = styled.div`
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-top: 12px;
  display: inline-block;
`;

const PriceBreakdown = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #333;
`;

const PriceRow = styled.div<{ $isStrikethrough?: boolean }>`
  display: flex;
  justify-content: space-between;
  color: ${props => props.$isStrikethrough ? '#666' : '#999'};
  font-size: 0.9rem;
  margin-bottom: 8px;
  text-decoration: ${props => props.$isStrikethrough ? 'line-through' : 'none'};
`;

const PriceLabel = styled.span``;

const PriceValue = styled.span`
  font-weight: 600;
`;

const TotalRow = styled(PriceRow)`
  color: #4ade80;
  font-size: 1rem;
  font-weight: 700;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #333;
`;

const PaymentSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  color: white;
  font-size: 1rem;
  margin: 0 0 16px 0;
`;

const PaymentElementWrapper = styled.div`
  .StripeElement {
    background: #2a2a2a;
    border-radius: 8px;
    border: 1px solid #333;
    padding: 16px;
  }
`;

const SubmitButton = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  background: ${props => props.$disabled ? '#333' : '#4ade80'};
  color: ${props => props.$disabled ? '#999' : 'white'};
  border: none;
  border-radius: 8px;
  padding: 16px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$disabled ? '#333' : '#22c55e'};
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 0.9rem;
  margin-top: 12px;
  padding: 12px;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 6px;
  border: 1px solid rgba(239, 68, 68, 0.2);
`;

const SpinnerWrapper = styled.div`
  display: inline-block;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingSpinner: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <SpinnerWrapper style={{ width: size, height: size }}>
    ⟳
  </SpinnerWrapper>
);

// Types
interface SubscriptionTier {
  id: string;
  tier_name: string;
  tier: number;
  monthly: number;
  yearly: number;
  stripe_price_id_monthly?: string;
  stripe_price_id_yearly?: string;
}

interface CouponDetails {
  tier: number;
  billingPeriod: 'monthly' | 'yearly';
  discountPercent: number;
  duration: 'once' | 'repeating';
  durationMonths: number | null;
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  tier: SubscriptionTier;
  billingPeriod: 'monthly' | 'yearly';
  onSuccess: () => void;
  couponCode?: string;
}

// Payment Form Component (needs to be inside Elements provider)
const PaymentForm: React.FC<{
  tier: SubscriptionTier;
  billingPeriod: 'monthly' | 'yearly';
  subscriptionInfo: any;
  onSuccess: () => void;
  onError: (error: string) => void;
  couponCode?: string;
}> = ({ tier, billingPeriod, subscriptionInfo, onSuccess, onError, couponCode }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useUser();
  const [isProcessing, setIsProcessing] = useState(false);
  const [couponDetails, setCouponDetails] = useState<CouponDetails | null>(null);

  // Fetch coupon details if coupon code is provided
  useEffect(() => {
    const fetchCouponDetails = async () => {
      if (!couponCode) {
        setCouponDetails(null);
        return;
      }

      try {
        const response = await fetch(`${config.apiUrl}/coupons/validate-all`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: couponCode })
        });
        const data = await response.json();

        if (data.valid && data.coupons) {
          // Find the coupon for this specific tier and billing period
          const matchingCoupon = data.coupons.find(
            (c: CouponDetails) => c.tier === tier.tier && c.billingPeriod === billingPeriod
          );
          setCouponDetails(matchingCoupon || null);
        }
      } catch (error) {
        console.error('Error fetching coupon details:', error);
        setCouponDetails(null);
      }
    };

    fetchCouponDetails();
  }, [couponCode, tier.tier, billingPeriod]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      onError('Payment system not ready');
      return;
    }

    setIsProcessing(true);
    onError(''); // Clear previous errors

    try {
      // Confirm the setup - this creates and attaches the payment method
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/subscription?success=true`,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        throw new Error(confirmError.message || 'Payment setup failed');
      }

      if (setupIntent && setupIntent.status === 'succeeded') {
        // Payment method is now attached to customer, create the subscription
        const token = authService.getToken();
        const response = await fetch(`${config.apiUrl}/stripe/create-subscription`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            priceId: subscriptionInfo.priceId,
            paymentMethodId: setupIntent.payment_method,
            billingPeriod: billingPeriod,
            couponCode: couponCode
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Failed to create subscription');
        }

        console.log('Subscription created successfully:', result);

        // Track Subscribe event for successful payment
        const basePrice = getBasePrice();
        const currentPrice = getCurrentPrice();
        const futurePrice = getFuturePrice();

        if (window.fbq) {
          window.fbq('track', 'Subscribe', {
            value: currentPrice.toFixed(2), // Amount charged now (with coupon/trial discount)
            predicted_ltv: (futurePrice || basePrice).toFixed(2), // Recurring cost after discounts expire
            currency: 'USD',
            content_name: tier.tier_name,
            subscription_plan: billingPeriod,
            coupon_code: couponCode || null
          });
        }

        // Track Google Analytics event
        if (window.gtag) {
          window.gtag('event', 'purchase', {
            transaction_id: result.subscriptionId || result.id,
            value: currentPrice,
            currency: 'USD',
            items: [{
              item_id: subscriptionInfo.priceId,
              item_name: tier.tier_name,
              item_category: 'subscription',
              price: currentPrice,
              quantity: 1
            }],
            coupon: couponCode || undefined
          });
        }

        onSuccess();
      }
    } catch (error) {
      console.error('Payment error:', error);
      onError(error instanceof Error ? error.message : 'Payment failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const getBasePrice = () => {
    return billingPeriod === 'yearly' ? Number(tier.yearly) : Number(tier.monthly);
  };

  const getDiscountedPrice = () => {
    if (!couponDetails) return null;
    const basePrice = getBasePrice();
    const discount = (basePrice * couponDetails.discountPercent) / 100;
    return basePrice - discount;
  };

  const getCurrentPrice = () => {
    const discountedPrice = getDiscountedPrice();
    return discountedPrice !== null ? discountedPrice : getBasePrice();
  };

  const getFuturePrice = () => {
    if (!couponDetails || couponDetails.duration !== 'once') return null;
    return getBasePrice();
  };

  return (
    <form onSubmit={handleSubmit}>
      <TierSummary>
        <TierName>{tier.tier_name}</TierName>

        {couponDetails && (
          <CouponBadge>
            {couponCode?.toUpperCase()}: {couponDetails.discountPercent}% off
          </CouponBadge>
        )}

        <PriceBreakdown>
          {couponDetails && (
            <PriceRow $isStrikethrough>
              <PriceLabel>Regular price:</PriceLabel>
              <PriceValue>${getBasePrice()}</PriceValue>
            </PriceRow>
          )}

          <TotalRow>
            <PriceLabel>
              {couponDetails && couponDetails.duration === 'once'
                ? `First ${billingPeriod === 'yearly' ? 'year' : 'month'}:`
                : couponDetails && couponDetails.duration === 'repeating'
                ? `First ${couponDetails.durationMonths} months:`
                : 'Price:'}
            </PriceLabel>
            <PriceValue>${getCurrentPrice().toFixed(2)}</PriceValue>
          </TotalRow>

          {getFuturePrice() !== null && (
            <PriceRow>
              <PriceLabel>Then:</PriceLabel>
              <PriceValue>${getFuturePrice()}/{billingPeriod === 'yearly' ? 'year' : 'month'}</PriceValue>
            </PriceRow>
          )}

          {billingPeriod === 'yearly' && !couponDetails && (
            <PriceRow>
              <PriceLabel>Includes 3-day trial</PriceLabel>
            </PriceRow>
          )}
        </PriceBreakdown>
      </TierSummary>

      <PaymentSection>
        <SectionTitle>Payment Information</SectionTitle>
        <PaymentElementWrapper>
          <PaymentElement options={{
            layout: 'tabs',
            defaultValues: {
              billingDetails: {
                email: user?.email,
                name: user?.username,
              }
            }
          }} />
        </PaymentElementWrapper>
      </PaymentSection>

      <SubmitButton type="submit" $disabled={!stripe || isProcessing}>
        {isProcessing ? (
          <>
            <LoadingSpinner size={16} /> Processing...
          </>
        ) : (
          `Subscribe to ${tier.tier_name}`
        )}
      </SubmitButton>
    </form>
  );
};

// Main Payment Modal Component
export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  tier,
  billingPeriod,
  onSuccess,
  couponCode,
}) => {
  const { stripe } = useStripeContext();
  const { user } = useUser();
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  // Setup payment intent when modal opens
  useEffect(() => {
    console.log('PaymentModal useEffect:', { isOpen, stripe: !!stripe, user: !!user });
    if (isOpen && stripe && user) {
      setupPaymentIntent();
    }
  }, [isOpen, stripe, user, tier, billingPeriod]);

  const setupPaymentIntent = async () => {
    console.log('Setting up subscription payment...');
    setIsLoading(true);
    setError('');

    try {
      const token = authService.getToken();
      
      // Get the price ID based on billing period
      const priceId = billingPeriod === 'yearly' 
        ? tier.stripe_price_id_yearly 
        : tier.stripe_price_id_monthly;

      if (!priceId) {
        throw new Error('Price ID not found for selected plan');
      }

      // Get subscription setup info (doesn't create subscription yet)
      const response = await fetch(`${config.apiUrl}/stripe/create-subscription-setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ priceId }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to setup subscription');
      }

      // Store the setup info for Elements
      setSubscriptionInfo(result);
    } catch (error) {
      console.error('Setup error:', error);
      setError(error instanceof Error ? error.message : 'Failed to setup payment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccess = () => {
    onSuccess();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Subscribe to {tier.tier_name}</ModalTitle>
          <CloseButton onClick={onClose}>
            ✕
          </CloseButton>
        </ModalHeader>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'white' }}>
            <LoadingSpinner size={24} />
            <div style={{ marginTop: '12px' }}>Setting up payment...</div>
          </div>
        ) : subscriptionInfo && stripe ? (
          <Elements stripe={stripe} options={{
            clientSecret: subscriptionInfo.clientSecret,
            appearance: {
              theme: 'night',
              variables: {
                colorPrimary: '#4ade80',
                colorBackground: '#2a2a2a',
                colorText: '#ffffff',
                colorDanger: '#ef4444',
                borderRadius: '8px',
              },
            },
          }}>
            <PaymentForm
              tier={tier}
              billingPeriod={billingPeriod}
              subscriptionInfo={subscriptionInfo}
              onSuccess={handleSuccess}
              onError={setError}
              couponCode={couponCode}
            />
          </Elements>
        ) : null}
      </ModalContent>
    </ModalOverlay>
  );
};

export default PaymentModal;